import React from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image
} from '@react-pdf/renderer';

import Logo from '../../images/57logo.jpeg';
import QR from '../../images/QR.png';
import Footer from '../../images/certificates.jpg';

// Utility
const getDaySuffix = (day) => {
  const j = day % 10, k = day % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
};
const formatDay = (day) => {
  if (!day) return 'N.A.';
  return `Every ${day}${getDaySuffix(day)} of the month`;
};

// Styles
const styles = StyleSheet.create({
  page: {
    paddingTop: 100,
    paddingBottom: 80,
    paddingHorizontal: 30,
    fontSize: 10,
    lineHeight: 1.5,
    fontFamily: 'Helvetica',
    position: 'relative'
  },
  header: {
    position: 'absolute',
    top: 30,
    left: 30,
    right: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  logo: {
    width: 60,
    height: 60
  },
  qr: {
    width: 60,
    height: 60
  },
  headerTextContainer: {
    textAlign: 'center',
    flexGrow: 1
  },
  headerLine1: {
    fontSize: 12,
    fontWeight: 'bold'
  },
  headerLine2: {
    fontSize: 10
  },
  sectionContainer: {
    borderWidth: 1,
    borderColor: '#000',
    marginBottom: 10
  },
  sectionHeader: {
    backgroundColor: '#f0f0f0',
    fontSize: 11,
    fontWeight: 'bold',
    // textDecoration: 'underline',
    padding: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#000'
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ccc'
  },
  cellLabel: {
    width: '45%',
    padding: 5,
    fontWeight: 'bold',
    borderRightWidth: 1,
    borderRightColor: '#ccc'
  },
  cellValue: {
    width: '55%',
    padding: 5
  },
  footerImage: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    height: 40
  },
  pageNumber: {
    position: 'absolute',
    bottom: 10,
    textAlign: 'center',
    width: '100%',
    fontSize: 8,
    color: 'gray'
  }
});

const TableRow = ({ label, value }) => (
  <View style={styles.row}>
    <Text style={styles.cellLabel}>{label}</Text>
    <Text style={styles.cellValue}>{value || 'N.A.'}</Text>
  </View>
);

const Section = ({ title, rows }) => (
  <View style={styles.sectionContainer}>
    <Text style={styles.sectionHeader}>{title}</Text>
    {rows.map((row, idx) => (
      <TableRow key={idx} label={row.label} value={row.value} />
    ))}
  </View>
);

const KETPdf = ({ kets = [], userDetails = {}, siteAddress = "" }) => {
  const ket = kets[0] || {};

  const fullName = `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim();

console.log("siteAddress", siteAddress)
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header} fixed>
          <Image src={Logo} style={styles.logo} />
          <View style={styles.headerTextContainer}>
            <Text style={styles.headerLine1}>57 PEACE PROTECTION PTE LTD</Text>
            <Text style={styles.headerLine2}>2 SIMS CLOSE, #03-09 GEMINI @SIMS, SINGAPORE 387298</Text>
          </View>
          <Image src={QR} style={styles.qr} />
        </View>

        {/* Sections */}
        <Section
          title="Section A | Details of Employment"
          rows={[
            { label: 'Company Name', value: ket.companyName },
            { label: 'Employee Full Name', value: fullName },
            { label: 'Employee NRIC/FIN', value: userDetails.nricOrFin },
            { label: 'Employment Start Date', value: ket.employmentStartDate },
            { label: 'Employment End Date', value: ket.employmentEndDate },
            { label: 'Job Title', value: userDetails.designation },
            { label: 'Main Duties and Responsibilities', value: ket.duties },
            { label: 'Employment Type', value: ket.employmentType },
            { label: 'Place of Work', value: siteAddress || 'Beside Blk 101, Woodlands Street 13, Singapore 730101' }
          ]}
        />

        <Section
          title="Section B | Working Hours and Rest Days"
          rows={[
            { label: 'Details of Working Hours', value: ket.workingHours },
            { label: 'Number of Working Days Per Week', value: ket.noOfWorkingDaysPerWeek },
            { label: 'Break During Work', value: ket.breakHours },
            { label: 'Rest Day (Specify)', value: ket.restDaysPerWeek }
          ]}
        />

        <Section
          title="Section C | Salary"
          rows={[
            { label: 'Salary Period', value: ket.salaryPeriod },
            { label: 'Overtime Payment Period', value: ket.overtimeSalaryPeriod },
            { label: 'Date(s) of Salary Payment', value: formatDay(ket.datesOfSalaryPayment) },
            { label: 'Date(s) of Overtime Payment', value: formatDay(ket.datesOfOvertimePayment) },
            { label: 'Effective Date', value: ket.salaryEffectiveDate },
            { label: 'Basic Rate of Pay', value: `$${ket.basicRateOfPay}` },
            { label: 'Gross Rate of Pay', value: `$${ket.basicRateOfPay}` },
            { label: 'Overtime Rate of Pay', value: `$${ket.overtimeRateOfPay}` }
          ]}
        />

        <Section
          title="Section D | Leave and Medical Benefits"
          rows={[
            { label: 'Paid Annual Leave Per Year', value: ket.paidAnualLeavePerYear },
            { label: 'Paid Outpatient Sick Leave Per Year', value: ket.paidSickLeavePerYear },
            { label: 'Paid Hospitalisation Leave Per Year', value: ket.paidHospitalisationLeavePerYear },
            { label: 'Others', value: ket.others },
            { label: 'Medical Benefits', value: ket.medicalBenefits }
          ]}
        />

        <Section
          title="Section E | Others"
          rows={[
            { label: 'Length of Probation', value: ket.lengthOfProbation },
            { label: 'Probation Start Date', value: ket.probationStartDate },
            { label: 'Probation End Date', value: ket.probationEndDate },
            { label: 'Termination Period During Probation', value: ket.terminationDuringProbation },
            { label: 'Termination Period After Confirmation', value: ket.terminationAfterConfirmation }
          ]}
        />

        {/* Footer Image */}
        <Image src={Footer} style={styles.footerImage} fixed />

        {/* Page Number */}
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

export default KETPdf;
